import { useCallback, useEffect, useMemo, useState } from "react";
import { DeleteTwoTone, EditTwoTone } from "@ant-design/icons";
import {
  message,
  Typography,
  Dropdown,
  Menu,
  Popconfirm,
} from "antd";
import ListTable from "../../common/ListTable";
import AppContent from "../../common/AppContent";
import AppPageHeader from "../../common/PageHeader";
import ExtraContent from "../Configuration/CommonComponents/ExtraContent";
import FilterComponent from "../Configuration/CommonComponents/FilterComponent";
import {
  getData,
  deleteData,
} from "../../apiServices/common";
import {
  HOURLY_TARGET_SETUP_GARMENTS_TRACKING_LIST,
  HOURLY_TARGET_SETUP_GARMENTS_TRACKING_DELETE,
  HOURLY_TARGET_SETUP_GARMENTS_TRACKING_BULK_UPLOAD,
} from "../../apiServices/API_ENDPOINTS";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import moment from "moment";

const GarmentsTrackingHourlyTarget = () => {
  // Router
  const location = useLocation();
  const navigate = useNavigate();
  const routerPath = location ? location.pathname : null;

  // States
  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tableError, setTableError] = useState(null);
  const [metaData, setMetaData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Antd
  const { Title } = Typography;

  // Redux
  const filterData = useSelector(({ filter }) => filter.filterData);

  // Filter open/close status update in localStorage
  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      parsedFilterStatus[routerPath] = true;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    } else {
      parsedFilterStatus[routerPath] = false;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    }
  }, [showFilter, routerPath, location]);

  const getAllHourlyTargets = useCallback(
    async (filterValues) => {
      setLoading(true);
      setTableError(null);

      const query = `${HOURLY_TARGET_SETUP_GARMENTS_TRACKING_LIST}?page=${currentPage}`;
      const bodyData = {
        ...filterValues,
      };

      const response = await getData(query, false, bodyData);
      if (response?.status === 200 && response?.data?.code === 200) {
        setLoading(false);
        setMetaData({
          defaultPageSize: response?.data?.data?.per_page,
          current: response?.data?.data?.current_page,
          defaultCurrent: response?.data?.data?.current_page,
          total: response?.data?.data?.total,
        });
        setTableData(response?.data?.data?.data);
      } else {
        setLoading(false);
        setTableError("Could not load this list.");
        message.error("Error getting hourly target data");
      }
    },
    [currentPage],
  );

  // Effects
  useEffect(() => {
    getAllHourlyTargets(filterData);
  }, [refresh, filterData, getAllHourlyTargets]);

  const onModalOpen = () => {
    navigate("/digitalization/garments-tracking-hourly-target/create");
  };

  const refetch = () => {
    setRefresh(refresh + 1);
  };

  const deleteSelectedHourlyTarget = async (id) => {
    setLoading(true);
    const response = await deleteData(
      `${HOURLY_TARGET_SETUP_GARMENTS_TRACKING_DELETE}/${id}`,
    );

    if (response && response?.code === 200) {
      setLoading(false);
      message.success("Hourly Target Deleted Successfully");
      refetch();
    } else {
      setLoading(false);
      message.error("Something went wrong");
    }
  };

  const openPageForEdit = (record) => {
    navigate(`/digitalization/garments-tracking-hourly-target/edit/${record?.id}`);
  };

  const changePage = (newPage) => {
    setCurrentPage(newPage);
    refetch();
  };

  const tableColumns = [
    {
      title: "Entry Date",
      dataIndex: ["entry_date"],
      key: "entry_date",
      render: (text) => (text ? moment(text).format("DD-MMM-YYYY") : "-"),
    },
    {
      title: "Line",
      dataIndex: ["line_info", "name"],
      key: "line_name",
    },
    {
      title: "User",
      dataIndex: ["user_info", "name"],
      key: "user_name",
    },
    {
      title: "ID Card No",
      dataIndex: ["user_id_card_no"],
      key: "user_id_card_no",
    },
    {
      title: "Hourly Target Qty",
      dataIndex: ["hourly_target_qty"],
      key: "hourly_target_qty",
    },
    {
      title: "Action",
      key: "action",
      width: "10%",
      render: (_, record) => {
        return (
          <Dropdown.Button
            overlay={
              <Menu
                items={[
                  {
                    label: (
                      <Popconfirm
                        title="Are you sure you want to delete?"
                        onConfirm={() => {
                          deleteSelectedHourlyTarget(record?.id);
                        }}
                      >
                        <span>
                          <DeleteTwoTone />
                        </span>
                        <span
                          style={{
                            color: "red",
                            marginLeft: 5,
                          }}
                        >
                          Delete
                        </span>
                      </Popconfirm>
                    ),
                    key: "1",
                  },
                  {
                    label: (
                      <>
                        <span>
                          <EditTwoTone />
                        </span>
                        <span
                          type="button"
                          style={{ marginLeft: 5 }}
                          onClick={() => {
                            openPageForEdit(record);
                          }}
                        >
                          Edit
                        </span>
                      </>
                    ),
                    key: "2",
                  },
                ]}
              />
            }
          ></Dropdown.Button>
        );
      },
    },
  ];

  const memoizedCustomFilter = useMemo(
    () => (
      <FilterComponent
        tableData={tableData}
        filterAPI={getAllHourlyTargets}
        showFilter={showFilter}
        setShowFilter={setShowFilter}
      />
    ),
    [showFilter],
  );

  return (
    <>
      <AppContent
        header={
          <AppPageHeader
            extra={
              <ExtraContent
                refresh={refresh}
                setRefresh={setRefresh}
                handleAddItemOpen={onModalOpen}
                addButtonText="Add New"
                showFilter={showFilter}
                setShowFilter={setShowFilter}
                hasImportButton={true}
                uploadAPI={HOURLY_TARGET_SETUP_GARMENTS_TRACKING_BULK_UPLOAD}
                acceptedFileTypes=".xlsx,.xls"
                afterUploadCustom={() => {
                  refetch();
                }}
              />
            }
            title={<Title level={5}>Garments Tracking Hourly Target</Title>}
          />
        }
        content={
          <>
            {memoizedCustomFilter}

            <div className="table" style={{ marginTop: 10 }}>
              <ListTable
                loading={loading}
                error={tableError}
                onRetry={() => refetch()}
                emptyText="No records to show yet."
                dataSource={tableData}
                columns={tableColumns}
                rowKey="id"
                pagination={{
                  showSizeChanger: false,
                  defaultPageSize: metaData?.defaultPageSize,
                  current: metaData?.current,
                  onChange: (newPage) => {
                    changePage(newPage);
                  },
                  defaultCurrent: metaData?.defaultCurrent,
                  total: metaData?.total,
                }}
              />
            </div>
          </>
        }
      />
    </>
  );
};

export default GarmentsTrackingHourlyTarget;
